/**
 * The row model behind the personalisation panel: one row per position, and the
 * single refusal the contract cannot phrase.
 *
 * ## The rows are the template's, and the bindings are the campaign's
 *
 * A row exists because the template has a position, not because somebody added
 * one — so there is no "add a row" here and no way to invent `{{7}}` for a
 * template that never wrote it. `rowsForTemplate` takes the positions the
 * server derived (`campaign.whatsapp.template.requiredPositions`, from
 * `requiredWaPositionsForDocument`, the one implementation) and the bindings
 * already stored, and produces the union of the two, ascending.
 *
 * **The union, and not just the required list.** A template edited under a
 * draft can *drop* a position, and the binding for it stays in the column: if
 * rows came only from `requiredPositions`, that binding would be invisible,
 * un-removable, and still submitted by every save — and `wa_bindings_unused`
 * would keep advising about a row nobody could see. It gets a row, so it can be
 * removed.
 *
 * ## Why a row's source is a kind plus a column rather than one string
 *
 * The wire shape is one string — `name`, `email`, `phoneE164` or
 * `extras.<column>`, slice 4's grammar, the same on both channels. But
 * `extras.` is a *prefix waiting for a column*, and a Select whose fourth
 * option carried a literal `extras.Company` would bind a column most workspaces
 * do not have the moment it was picked: `WaComposer`'s equivalent offers
 * exactly that string because there it is inserted as editable text, and here
 * it would be submitted. So the choice and the column are two controls, and
 * {@link sourceForRow} is the only place they are joined back into the string
 * the contract takes.
 *
 * ## `required` has three values, and the third one is the whole point
 *
 * `true` — the template asks for this position. `false` — it used to and does
 * not any more, so the send will ignore whatever is bound here. **`null` — the
 * template could not be read, so nobody knows.**
 *
 * The third was a `false` until slice 49's review. With `template === null` the
 * panel called this with an empty position list, every row came back
 * `required: false`, and every stored binding drew a warn-toned `ignored` chip
 * — telling a reader that the send would discard a binding it will in fact use,
 * on the one screen whose header promises that "the template dropped this
 * position" and "nobody could read the template" are never collapsed. A
 * tri-state costs one union member; the collapse cost a false statement about
 * every row.
 *
 * ## The one refusal here, and the ones that are not
 *
 * Exactly one thing can go wrong that `waBindingListSchema` cannot say well:
 * `extras.` with no column typed. The contract would quote back `Wend does not
 * know the field “extras.”`, which quotes the person's own empty box at them.
 *
 * **An unbound position is not refused here, and that is deliberate.** It was,
 * until slice 49's review pointed out that the panel's own Remove button — the
 * only way to undo a field chosen by mistake — produced a state Save could
 * never accept: remove the field on a required row, press Save, be refused,
 * with no way forward but to re-pick the field you were trying to drop. A
 * half-answered campaign is a perfectly good draft. It is a **schedule-time**
 * blocker, not a save-time one, and the server already refuses the schedule
 * with `wa_bindings_incomplete`, naming the position.
 *
 * Everything else is the contract's and is printed in the contract's own words:
 * the required fallback, the duplicate position, an unparseable source, the
 * 1–99 range. That is deliberate. The fallback sentence in particular is the
 * one this slice turns on, and the API refuses with it at save; a second copy
 * written here would be the copy that drifts, and the drift would show up as
 * the screen and the server giving two different accounts of the same field.
 */
import {
  CONTACT_VARIABLE_NAMES,
  EXTRAS_VARIABLE_PREFIX,
  waBindingState,
  type WaVariableBinding,
} from '@wend/shared';
import type { SelectOption } from '@wend/ui';

/** `{{1}}` — the token exactly as the template writes it and the bubble shows it. */
export function waPositionToken(position: number): string {
  return `{{${String(position)}}}`;
}

/**
 * The Select's fourth value: a marker for "an uploaded column", never a source.
 *
 * `EXTRAS_VARIABLE_PREFIX` itself, which is not a valid source — `extras.` with
 * nothing after it fails `parseWaBindingSource` — so a row still sitting on it
 * cannot be submitted by accident. Radix reserves `''`, so a sentinel is needed
 * regardless; this one is the sentinel that is already wrong on purpose.
 */
export const EXTRAS_SOURCE = EXTRAS_VARIABLE_PREFIX;

/**
 * The fields a position can stand for, derived from the contact contract.
 *
 * The three contact labels are `WaComposer`'s, word for word, so the template
 * editor and the campaign composer name the same field the same way. They are
 * **copied rather than imported**: `VARIABLE_SOURCES` is a module-private const
 * in another route's file, and reaching into it would make that file's internals
 * an export nobody declared. Built from `CONTACT_VARIABLE_NAMES` — the contact
 * schema's own keys — for the reason `WaComposer`'s comment gives: a
 * hand-written list goes stale the first time a field is renamed, and this one
 * stops compiling instead.
 */
export const WA_BINDING_SOURCE_OPTIONS: readonly SelectOption[] = [
  ...CONTACT_VARIABLE_NAMES.map((name) => ({
    value: name,
    label:
      name === 'name'
        ? 'Contact’s name'
        : name === 'email'
          ? 'Contact’s email address'
          : 'Contact’s phone number',
  })),
  { value: EXTRAS_SOURCE, label: 'A column from your upload' },
];

export interface WaBindingRow {
  readonly position: number;
  /** A contact field name, {@link EXTRAS_SOURCE}, or null for "not chosen". */
  readonly source: string | null;
  /** What goes after `extras.`. Ignored unless `source` is {@link EXTRAS_SOURCE}. */
  readonly column: string;
  readonly fallback: string;
  /**
   * True when the template asks for this position, false when it dropped it,
   * **null when the template could not be read at all.** Never collapse the
   * third into the second: see the header.
   */
  readonly required: boolean | null;
}

/** Splits a stored `source` back into the two controls that produced it. */
function rowFor(
  position: number,
  binding: WaVariableBinding | undefined,
  required: boolean | null,
): WaBindingRow {
  if (binding === undefined) {
    return { position, source: null, column: '', fallback: '', required };
  }
  if (binding.source.startsWith(EXTRAS_VARIABLE_PREFIX)) {
    return {
      position,
      source: EXTRAS_SOURCE,
      column: binding.source.slice(EXTRAS_VARIABLE_PREFIX.length),
      fallback: binding.fallback,
      required,
    };
  }
  return { position, source: binding.source, column: '', fallback: binding.fallback, required };
}

/**
 * One row per position the template needs, plus one per binding it no longer
 * needs. Ascending, deduplicated.
 *
 * **`requiredPositions === null` is "the template could not be read"**, and it
 * is not the same argument as `[]`. It draws the bindings that are stored —
 * they are still the campaign's, and Remove is still the way to clear one — and
 * marks every row `required: null`, so nothing on screen makes a claim about a
 * document nobody managed to open.
 *
 * Which positions the template no longer uses comes from `waBindingState` in
 * `@wend/shared`, the same function the schedule gate and the campaign read
 * call. Slice 49's review found that rule written out twice; a third copy here
 * would be the one that drifts, and the drift renders as a row the panel calls
 * ignored while the send fills it.
 */
export function rowsForTemplate(
  requiredPositions: readonly number[] | null,
  bindings: readonly WaVariableBinding[],
): WaBindingRow[] {
  const bound = new Map<number, WaVariableBinding>();
  // Last write wins, which matches nothing in particular — the contract refuses
  // a duplicate position outright, so a column holding two is a row written
  // directly rather than through the API, and it is shown rather than merged.
  for (const binding of bindings) bound.set(binding.position, binding);

  if (requiredPositions === null) {
    return [...bound.keys()]
      .sort((a, b) => a - b)
      .map((position) => rowFor(position, bound.get(position), null));
  }

  const unused = new Set(waBindingState(requiredPositions, bindings).unused);
  const positions = [...new Set([...requiredPositions, ...bound.keys()])].sort((a, b) => a - b);
  return positions.map((position) => rowFor(position, bound.get(position), !unused.has(position)));
}

/** The wire shape of a row's source, or null when the row is not bound yet. */
export function sourceForRow(row: WaBindingRow): string | null {
  if (row.source === null) return null;
  if (row.source !== EXTRAS_SOURCE) return row.source;
  const column = row.column.trim();
  return column === '' ? null : `${EXTRAS_VARIABLE_PREFIX}${column}`;
}

/** Whether this row has enough to be worth sending to the preview. */
export function isRowBound(row: WaBindingRow): boolean {
  return sourceForRow(row) !== null;
}

/**
 * The rows, as the contract's array — **ascending, and paired with the
 * positions that produced it.**
 *
 * The positions come back because the array index is *not* the row index: a
 * template needing `{{1}}` and `{{2}}` with only `{{2}}` bound submits one
 * binding at index 0, and a server refusal on `waBindings.0.fallback` belongs
 * on the `{{2}}` row. Keying the panel's errors by position and translating the
 * index through this list is what makes a refusal land on the token the reader
 * is looking at, which is the whole of the house rule about refusals.
 *
 * A row with no source is left out entirely rather than submitted empty — an
 * absent binding is a position nobody has answered, and `[]` on a WhatsApp
 * campaign is the honest shape for that.
 */
export function submissionForRows(rows: readonly WaBindingRow[]): {
  bindings: WaVariableBinding[];
  positions: number[];
} {
  const bindings: WaVariableBinding[] = [];
  const positions: number[] = [];
  for (const row of rows) {
    const source = sourceForRow(row);
    if (source === null) continue;
    bindings.push({ position: row.position, source, fallback: row.fallback });
    positions.push(row.position);
  }
  return { bindings, positions };
}

/** Where a row's message for one control lives in the panel's error map. */
export function rowErrorKey(position: number, field: 'source' | 'column' | 'fallback'): string {
  return `waBindings.${String(position)}.${field}`;
}

/**
 * The refusal a save can make without asking the server — and only that one.
 *
 * Keyed by {@link rowErrorKey}, so it lands on the same control a server
 * refusal would, and it names the position by its token, which is the chip on
 * the row and the token in the bubble, so there is never a question of which
 * row a sentence is about.
 *
 * **A row with no source at all is not refused**, whatever `required` says. It
 * is simply left out of the submission by {@link submissionForRows}, which is
 * the honest shape for a position nobody has answered yet — and it is what
 * makes the Remove button on a required row mean something, because the state
 * it produces has to be one Save accepts. The schedule is where an unanswered
 * position stops being a draft's business: `wa_bindings_incomplete`, from the
 * server, naming the position.
 */
export function refusalsForRows(rows: readonly WaBindingRow[]): Record<string, string> {
  const refusals: Record<string, string> = {};
  for (const row of rows) {
    if (row.source !== EXTRAS_SOURCE || row.column.trim() !== '') continue;
    refusals[rowErrorKey(row.position, 'column')] =
      `Name the uploaded column that fills ${waPositionToken(row.position)}.`;
  }
  return refusals;
}

/**
 * Turns the API's and the contract's `waBindings.<index>.<field>` paths into
 * this panel's position-keyed ones. A path this panel cannot place — a bare
 * `waBindings`, or an index no longer in the submission — is returned under its
 * own key for the panel's form-level alert, because a refusal that cannot be
 * drawn beside a control must still be drawn.
 */
export function placeBindingErrors(
  errors: Readonly<Record<string, string>>,
  positions: readonly number[],
): Record<string, string> {
  const placed: Record<string, string> = {};
  for (const [path, message] of Object.entries(errors)) {
    const match = /^waBindings\.(\d+)\.(source|fallback|position)$/.exec(path);
    const position = match === null ? undefined : positions[Number(match[1])];
    if (match === null || position === undefined) {
      placed[path] = message;
      continue;
    }
    // A refusal about the position itself — a duplicate, or one out of range —
    // is drawn on the control that chose it. The chip is not a control and the
    // fallback box is a different question.
    const field = match[2] === 'fallback' ? 'fallback' : 'source';
    placed[rowErrorKey(position, field)] = message;
  }
  return placed;
}
